import type { BodyImageEventName } from '@types';

export type BodyImageEventPanel = 'start' | 'controllo-nuovo';

export type BodyImageEventDefinition = {
  /** Ably message name / tracking eventName */
  name: BodyImageEventName;
  /** Label shown to clinicians in tracking UI and notifications */
  label: string;
  /** Device command field when present in payload */
  command: string | null;
  /** Where the event is triggered in the webapp */
  panel: BodyImageEventPanel;
  /** Short description of what the event does */
  description: string;
};

/**
 * Canonical map of Body Image realtime events.
 * Keep this in sync when adding/renaming Ably events or UI controls.
 */
export const BODY_IMAGE_EVENT_MAP: Record<
  BodyImageEventName,
  BodyImageEventDefinition
> = {
  start_bodyimage: {
    name: 'start_bodyimage',
    label: 'Avvio sessione',
    command: 'start_bodyimage',
    panel: 'start',
    description:
      'Avvia lo scenario Body Image sul device con le misure utente.',
  },
  'bodyImage:mannequins:home': {
    name: 'bodyImage:mannequins:home',
    label: 'Home manichini',
    command: 'bodyImage:mannequins:home',
    panel: 'controllo-nuovo',
    description: 'Mostra tutti i manichini (figure 1–9) sul device.',
  },
  'bodyImage:mannequin': {
    name: 'bodyImage:mannequin',
    label: 'Seleziona manichino',
    command: 'bodyImage:mannequin',
    panel: 'controllo-nuovo',
    description: 'Mostra un singolo manichino preset (value = figura 1–9).',
  },
  'bodyImage:mannequins:compare': {
    name: 'bodyImage:mannequins:compare',
    label: 'Affianca manichini',
    command: 'bodyImage:mannequins:compare',
    panel: 'controllo-nuovo',
    description:
      'Affianca figura preset selezionata (sx) con manichino custom da misure (dx).',
  },
  'bodyImage:rotate': {
    name: 'bodyImage:rotate',
    label: 'Rotazione manichini',
    command: 'bodyImage:rotate',
    panel: 'controllo-nuovo',
    description:
      'Ruota tutti i manichini di N gradi (value con segno: negativo=sx, positivo=dx).',
  },
};

export const BODY_IMAGE_EVENT_NAMES = Object.keys(
  BODY_IMAGE_EVENT_MAP
) as BodyImageEventName[];

/** Read-only labels for historical tracking rows (no longer published). */
const BODY_IMAGE_LEGACY_EVENT_LABELS: Record<string, string> = {
  'bodyImage:showOwnAvatar': 'Mostra avatar',
  'bodyImage:showComparisonGroup': 'Mostra gruppo',
  temporaryfigure_bodyimage: 'Manichino temporaneo',
  rotate_bodyimage: 'Rotazione',
  bodyselected_bodyimage: 'Selezione figura',
  'bodyImage:showComparisonAvatar': 'Mostra manichino (legacy)',
  'bodyImage:mannequin:comparison': 'Mostra manichino',
};

/** @deprecated Prefer constant `bodyImage:mannequin`; figure lives in payload.value */
export const bodyImageMannequinSelectEventName = (_figure?: number) => {
  void _figure;
  return 'bodyImage:mannequin';
};

/** @deprecated Prefer constant `bodyImage:rotate`; degrees live in payload.value */
export const bodyImageRotateEventName = (_signedDegrees?: number) => {
  void _signedDegrees;
  return 'bodyImage:rotate';
};

const formatRotateLabel = (signedDegrees: number) => {
  const degrees = Math.abs(signedDegrees);
  return signedDegrees < 0
    ? `Rotazione sinistra (${degrees}°)`
    : `Rotazione destra (${degrees}°)`;
};

export const formatBodyImageEventLabel = (
  eventName: string,
  eventPayload?: Record<string, unknown> | null
): string => {
  // Legacy dynamic name: bodyImage:mannequin:3
  const legacyMannequinMatch = /^bodyImage:mannequin:(\d+)$/.exec(eventName);
  if (legacyMannequinMatch != null) {
    const figure = Number(legacyMannequinMatch[1]);
    if (Number.isInteger(figure) && figure > 0) {
      return `Seleziona manichino ${figure}`;
    }
  }

  // Legacy dynamic name: bodyImage:rotate:-90
  const legacyRotateMatch = /^bodyImage:rotate:(-?\d+)$/.exec(eventName);
  if (legacyRotateMatch != null) {
    const degrees = Number(legacyRotateMatch[1]);
    if (Number.isFinite(degrees) && degrees !== 0) {
      return formatRotateLabel(degrees);
    }
  }

  const definition = BODY_IMAGE_EVENT_MAP[eventName as BodyImageEventName];
  const baseLabel =
    definition?.label ?? BODY_IMAGE_LEGACY_EVENT_LABELS[eventName] ?? eventName;

  if (eventPayload == null) {
    return baseLabel;
  }

  if (eventName === 'bodyImage:mannequin') {
    const value = Number(eventPayload.value ?? eventPayload.figure);
    if (!Number.isNaN(value) && value > 0) {
      return `Seleziona manichino ${value}`;
    }
  }

  if (eventName === 'bodyImage:mannequin:comparison') {
    const label =
      typeof eventPayload.label === 'string' ? eventPayload.label.trim() : '';
    if (label !== '') {
      return `Mostra manichino: ${label}`;
    }
    const comparisonId = Number(eventPayload.comparisonId);
    if (Number.isInteger(comparisonId) && comparisonId > 0) {
      return `Mostra manichino #${comparisonId}`;
    }
  }

  if (eventName === 'bodyImage:rotate') {
    const value = Number(eventPayload.value ?? eventPayload.rotate);
    if (!Number.isNaN(value)) {
      if (value === 0) {
        return 'Rotazione (0°)';
      }
      return formatRotateLabel(value);
    }
  }

  if (eventName === 'rotate_bodyimage') {
    const rotate = Number(eventPayload.rotate);
    if (!Number.isNaN(rotate) && rotate !== 0) {
      return formatRotateLabel(rotate);
    }
  }

  if (eventName === 'bodyselected_bodyimage') {
    const figure = Number(eventPayload.figure);
    if (!Number.isNaN(figure) && figure > 0) {
      return `Selezione figura ${figure}`;
    }
  }

  if (eventName === 'temporaryfigure_bodyimage') {
    const step = Number(eventPayload.bodyimage_temporaryfigure_step);
    if (!Number.isNaN(step) && step > 0) {
      return `Manichino temporaneo (figura ${step})`;
    }
  }

  if (eventName === 'bodyImage:showComparisonAvatar') {
    const label =
      typeof eventPayload.label === 'string' ? eventPayload.label.trim() : '';
    if (label !== '') {
      return `Mostra manichino: ${label}`;
    }
  }

  if (eventName === 'bodyImage:mannequins:compare') {
    const figures = Array.isArray(eventPayload.figures)
      ? eventPayload.figures
      : null;

    if (figures != null && figures.length >= 2) {
      const left = figures[0];
      const right = figures[1];

      const leftValue =
        left != null && typeof left === 'object' && !Array.isArray(left)
          ? Number((left as Record<string, unknown>).value)
          : Number(left);

      let rightLabel = '';
      if (right != null && typeof right === 'object' && !Array.isArray(right)) {
        const rightObj = right as Record<string, unknown>;
        if (
          typeof rightObj.label === 'string' &&
          rightObj.label.trim() !== ''
        ) {
          rightLabel = rightObj.label.trim();
        } else if (rightObj.comparisonId != null) {
          rightLabel = `#${rightObj.comparisonId}`;
        } else {
          rightLabel = 'custom';
        }
      } else if (Number.isInteger(Number(right)) && Number(right) > 0) {
        rightLabel = `figura ${Number(right)}`;
      }

      if (Number.isInteger(leftValue) && leftValue > 0 && rightLabel !== '') {
        return `Affianca (figura ${leftValue} | ${rightLabel})`;
      }
    }

    const leftFigure = Number(eventPayload.leftFigure);
    const rightFigure = Number(eventPayload.rightFigure);
    if (
      Number.isInteger(leftFigure) &&
      leftFigure > 0 &&
      Number.isInteger(rightFigure) &&
      rightFigure > 0
    ) {
      return `Affianca manichini (${leftFigure} | ${rightFigure})`;
    }
  }

  return baseLabel;
};
